import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { subscribeFleetDriver } from "../../services/fleetDriverService";
import { Button, Divider, Input, Modal, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { CiDeliveryTruck } from "react-icons/ci";
import { IoDocumentTextOutline } from "react-icons/io5";
import { LuCalendarCheck } from "react-icons/lu";
import { CiEdit } from "react-icons/ci";
import { FiCopy } from "react-icons/fi";
import { FaLink, FaLinkSlash } from "react-icons/fa6";
import { TbBrandWechat } from "react-icons/tb";
import { RightOutlined } from "@ant-design/icons";
import {
  API_Fleet_ID_planned_route,
  API_Fleet_ID_planned_route_preview,
  API_FleetDriver_ID_patch_cards_OT,
  API_FleetDriver_ID_get_onecard_OT,
  API_PublicLink_patch,
  API_PublicLink_post,
  API_REQUEST_HEADERS_BASEURL,
  assertFleetOperationsAllowed,
} from "../../utils/Constants";
import ProgressStep from "../progressSteps/ProgressSteps";
import "./TransportOrderDetailModal.css";
import { useTranslation } from "react-i18next";
import ImageGallery from "../imageGallery/imageGallery";
import OrderForm from "../orderForm/orderForm";
import { parseScheduledDate } from "../../utils/orderScheduledDate";
import { useGlobalToast } from "../../context/ToastProvider";
import { useChat } from "../../context/ChatContext";
import MapWithLayers from "../map/MapWithLayer";
import { GiRoad } from "react-icons/gi";
import { MdOutlineAddRoad } from "react-icons/md";
import NotificationBadge from "../notificationBadge/notificationBadge";
import IncidentModal from "../incidentList/incidentModal";
import "../map/MapWithLayer.css";
import { isColorDark } from "../sideBar/sideBar";
import { collection, doc, getDoc, getDocs, onSnapshot, orderBy, query, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { getDriverPresence } from "../../utils/driverPresence";
import ChangeActiveDriver from "./ChangeActiveDriver";

const ACTUAL_ROUTE_GAP_THRESHOLD_KM = 15;
const PLANNED_OR_ESTIMATED_ROUTE_COLOR = "rgba(255, 165, 0, 0.8)";

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const haversineDistanceKm = (from, to) => {
  const earthRadiusKm = 6371;
  const toRadians = (value) => (value * Math.PI) / 180;
  const dLat = toRadians(to.latitude - from.latitude);
  const dLng = toRadians(to.longitude - from.longitude);
  const lat1 = toRadians(from.latitude);
  const lat2 = toRadians(to.latitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const splitRouteByGap = (points, thresholdKm) => {
  if (!Array.isArray(points) || points.length === 0) return [];
  const segments = [];
  let current = [points[0]];

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const next = points[index];
    const gapKm = haversineDistanceKm(previous, next);

    if (gapKm > thresholdKm) {
      if (current.length >= 2) {
        segments.push(current);
      }
      current = [next];
      continue;
    }

    current.push(next);
  }

  if (current.length >= 2) {
    segments.push(current);
  }

  return segments;
};

const estimateGapPairs = (points, thresholdKm) => {
  const gaps = [];
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const gapKm = haversineDistanceKm(from, to);
    if (gapKm > thresholdKm) {
      gaps.push({ from, to });
    }
  }
  return gaps;
};

const pointPairCacheKey = (from, to) =>
  `${from.latitude.toFixed(6)},${from.longitude.toFixed(6)}|${to.latitude.toFixed(6)},${to.longitude.toFixed(6)}`;

const TransportOrderDetaiModal = ({ isOpen, onClose, transportOrderId, item, driverName }) => {
  const { getFleetID } = useContext(AuthContext);
  const fleetId = getFleetID();
  const [orderDetail, setOrderDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState("");
  const [inputLabel, setInputLabel] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [routeLayer, setRouteLayer] = useState([]);
  const [plannedRouteLayer, setPlannedRouteLayer] = useState([]);
  const [estimatedGapRoutes, setEstimatedGapRoutes] = useState([]);
  const [generatingRoute, setGeneratingRoute] = useState(false);
  const [showPlannedRoute, setShowPlannedRoute] = useState(false);
  const [isIncidentModalOpen, setIncidentModalOpen] = useState(false);
  const [actualRouteColor, setActualRouteColor] = useState("#1ce783");
  const [driverPresenceState, setDriverPresenceState] = useState(null);
  const gapRouteCacheRef = useRef(new Map());

  const newAxios = API_REQUEST_HEADERS_BASEURL;

  const primaryColor = "var(--chakra-colors-gandolapp-500)";

  const headerTextColor = isColorDark(primaryColor) ? "white" : "black";

  const { t } = useTranslation();
  const toast = useGlobalToast();
  const { openChat } = useChat();

  const formatOrderStatus = (status) => {
    if (!status) return "N/A";
    const key = status.toLowerCase();
    return t(`transportManagement.${key}`, {
      defaultValue: `${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    });
  };

  const formatOrderType = (type) => {
    if (!type) return "N/A";
    if (type === "CONTAINER_OUTBOUND") {
      return t("orderType.containerOutbound", { defaultValue: "Container Export" });
    }
    if (type === "CONTAINER_INBOUND") {
      return t("orderType.containerInbound", { defaultValue: "Container Import" });
    }
    if (type === "CONVENTIONAL_PICKUP") {
      return t("orderType.conventionalPickup", { defaultValue: "Conventional Pickup" });
    }
    if (type === "CONVENTIONAL_DELIVERY") {
      return t("orderType.conventionalDelivery", { defaultValue: "Conventional Delivery" });
    }
    return type;
  };

  const buildTruckflowPointRows = () => {
    const points = orderDetail?.truckflow?.points || {};
    const rows = [];
    const type = orderDetail?.type || "";

    if (type === "CONTAINER_INBOUND") {
      if (points.origin) rows.push({ label: t("mapEditor.pickupFullContainer"), point: points.origin });
      if (points.customer) rows.push({ label: t("mapEditor.deliveryGoodsLocation"), point: points.customer });
      if (points.gateIn) rows.push({ label: t("mapEditor.returnEmptyContainer"), point: points.gateIn });
    } else if (type === "CONTAINER_OUTBOUND") {
      if (points.origin) rows.push({ label: t("mapEditor.pickupEmptyContainer"), point: points.origin });
      if (points.customer) rows.push({ label: t("mapEditor.loadingGoodsLocation"), point: points.customer });
      if (points.gateIn) rows.push({ label: t("mapEditor.returnFullContainer"), point: points.gateIn });
    } else if (type === "CONVENTIONAL_PICKUP" || type === "CONVENTIONAL_DELIVERY") {
      const originPoint = points.origin || points.pickup;
      const customerPoint = points.customer || points.delivery;
      if (originPoint) rows.push({ label: t("mapEditor.pickUp"), point: originPoint });
      if (customerPoint) rows.push({ label: t("mapEditor.delivery"), point: customerPoint });
    }

    if (!rows.length && points) {
      Object.entries(points).forEach(([key, point]) => {
        if (!point) return;
        rows.push({ label: key, point });
      });
    }

    return rows;
  };

  const publicLink = orderDetail?.transportOrderToken || null;
  const hasPublicLink = Boolean(publicLink);
  const hasEDoc = Boolean(orderDetail?.freightDocumentId || item?.raw?.freightDocumentId);
  const orderViewPdfUrl = String(orderDetail?.viewPdfUrl || item?.raw?.viewPdfUrl || "").trim();
  const canOpenDriverActions = ["FINALIZED", "ACTIVE"].includes(orderDetail?.status) && Boolean(orderDetail?.userId);
  const showTruckflow = ["ACTIVE", "FINALIZED"].includes(String(orderDetail?.status || "").toUpperCase());
  const truckflowPointRows = buildTruckflowPointRows();
  const orderRefValue =
    orderDetail?.internalReference
    || item?.raw?.internalReference
    || orderDetail?.customerReference
    || orderDetail?.transportOrderRef
    || item?.ref
    || item?.raw?.transportOrderRef
    || transportOrderId;
  const resolvedDriverName = driverName || t("driverCard.unknownDriver");

  const buildEDocTemplates = (profile) => {
    switch (profile) {
      case "ECMR":
        return { pdfTemplate: "ECMR", docType: "WAYBILL", docTemplate: "WAYBILL" };
      case "EUROPEENNE":
        return { pdfTemplate: "EUROPEENNE", docType: "WAYBILL_FRA", docTemplate: "WAYBILL_FRA" };
      case "CARTA_DE_PORTE":
        return { pdfTemplate: "CARTA_DE_PORTE", docType: "WAYBILL_ESP", docTemplate: "WAYBILL_ESP" };
      case "DOCUMENTO_DE_CONTROL":
        return {
          pdfTemplate: "DOCUMENTO_DE_CONTROL",
          docType: "WAYBILL_ESP",
          docTemplate: "WAYBILL_ESP",
        };
      case "NAKLADNI_LIST_CZ":
        return {
          pdfTemplate: "NAKLADNI_LIST_CZ",
          docType: "WAYBILL_CZE",
          docTemplate: "WAYBILL_CZE",
        };
      case "LIEFERSCHEIN":
        return { pdfTemplate: "LIEFERSCHEIN", docType: "WAYBILL", docTemplate: "WAYBILL" };
      default:
        return {};
    }
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const root = window.getComputedStyle(document.documentElement);
    const color = root.getPropertyValue("--chakra-colors-gandolapp-500")?.trim();
    if (color) {
      setActualRouteColor(color);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const driverId = String(orderDetail?.userId || "").trim();
    if (!driverId || !fleetId) {
      setDriverPresenceState(null);
      return undefined;
    }

    const unsubscribe = subscribeFleetDriver(
      fleetId, driverId,
      (data) => {
        const { isOnline } = getDriverPresence(data?.isOnline, data?.lastSeen);
        const hasActiveOrder = Boolean(String(data?.currentOrder || "").trim());
        setDriverPresenceState({
          isOnline,
          hasActiveOrder,
        });
      },
      (error) => {
        console.error("Error loading driver presence for order detail map:", error);
        setDriverPresenceState(null);
      }
    );

    return () => unsubscribe();
  }, [isOpen, orderDetail?.userId, fleetId]);

  //Generar Public link
  const generatePublicLink = async () => {
    try {
      const apiEndpoint = API_PublicLink_post.replace("{fleetId}", fleetId).replace(
        "{transportOrderId}",
        transportOrderId
      );

      const response = await newAxios.post(apiEndpoint);
      const publicLink = response.data.link;

      // Copiar link
      await navigator.clipboard.writeText(publicLink);

      toast({
        title: t("orderDetail.linkCopied") || "Link copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      fetchOrderDetails();
    } catch (error) {
      console.error("Erro ao gerar link público:", error);
      toast({ error,
        title: t("orderDetail.linkCopyFailed") || "Failed to copy link",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const copyExistingPublicLink = async () => {
    try {
      const apiEndpoint = API_PublicLink_post.replace("{fleetId}", fleetId).replace(
        "{transportOrderId}",
        transportOrderId
      );

      const response = await newAxios.post(apiEndpoint);

      // Backend devuelve el MISMO link si el token es válido
      await navigator.clipboard.writeText(response.data.link);

      toast({
        title: t("orderDetail.linkCopied"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({ error,
        title: t("orderDetail.linkCopyFailed"),
        status: "error",
      });
    }
  };

  const deletePublicLink = async () => {
    try {
      const apiEndpoint = API_PublicLink_patch.replace("{fleetId}", fleetId).replace(
        "{transportOrderId}",
        transportOrderId
      );

      await API_REQUEST_HEADERS_BASEURL.patch(apiEndpoint);

      toast({
        title: t("orderDetail.linkDeleted"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      fetchOrderDetails();
    } catch (error) {
      toast({ error,
        title: t("orderDetail.linkDeleteFailed"),
        status: "error",
      });
    }
  };

  const generateEDocForOrder = async () => {
    try {
      const endpoint = API_FleetDriver_ID_patch_cards_OT.replace(
        "{fleetId}",
        fleetId
      ).replace("{fleetOT}", transportOrderId);

      await API_REQUEST_HEADERS_BASEURL.patch(endpoint, {
        generateEDocs: true,
        generateECMR: true,
      });

      toast({
        title: t("toast.eDocsGenerated", {
          defaultValue: "e-Doc generated successfully.",
        }),
        status: "success",
      });

      await fetchOrderDetails();
    } catch (error) {
      toast({ error,
        title:
          t("toast.eDocsGenerateFailed", {
            defaultValue: "Could not generate e-Docs for this order.",
          }),
        status: "warning",
      });
    }
  };

  const formatFirestoreTimestamp = (timestamp) => {
    return parseScheduledDate(timestamp)?.toLocaleString() || "N/A";
  };

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      if (!fleetId || !transportOrderId) {
        setOrderDetail(null);
        return;
      }

      const endpoint = API_FleetDriver_ID_get_onecard_OT.replace('{fleetId}', fleetId).replace('{fleetOT}', transportOrderId);
      const response = await API_REQUEST_HEADERS_BASEURL.get(endpoint);
      const data = response.data?.data;
      if (!data) {
        setOrderDetail(null);
        setLabel("");
        setInputLabel("");
        return;
      }

      setOrderDetail({...data, id: transportOrderId});
      setLabel(data.label || "");
      setInputLabel(data.label || "");
      return true;
    } catch (error) {
      console.error("Error al obtener detalles de la orden:", error);
      toast({ title: t('feedback.unavailable'), status: 'warning' });
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    fetchOrderDetails();
    fetchPlannedRoute();
    return undefined;
  }, [fleetId, isOpen, transportOrderId]);

  useEffect(() => {
    if (!isOpen) return undefined;

    if (!fleetId || !transportOrderId) {
      setRouteLayer([]);
      return undefined;
    }

    const actualRouteRef = collection(
      db,
      "fleets",
      fleetId,
      "transport_orders",
      transportOrderId,
      "actual_route"
    );
    const actualRouteQuery = query(actualRouteRef, orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(
      actualRouteQuery,
      (snapshot) => {
        const routePoints = snapshot.docs
          .map((routeDoc) => routeDoc.data() || {})
          .map((point) => ({
            latitude: toFiniteNumber(point.latitude),
            longitude: toFiniteNumber(point.longitude),
            timestamp: point.timestamp || null,
          }))
          .filter(
            (point) =>
              point.latitude != null &&
              point.longitude != null &&
              point.latitude >= -90 &&
              point.latitude <= 90 &&
              point.longitude >= -180 &&
              point.longitude <= 180
          );

        setRouteLayer(routePoints);
      },
      (error) => {
        console.error("Error subscribing to actual route:", error);
        setRouteLayer([]);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [fleetId, isOpen, transportOrderId]);

  const openPdf = (url) => {
    window.open(url, "_blank");
  };

  const updateLabelInOrder = async (newLabel) => {
    try {
      if (!fleetId || !transportOrderId) {
        return;
      }
      await assertFleetOperationsAllowed(fleetId);
      const orderRef = doc(db, "fleets", fleetId, "transport_orders", transportOrderId);
      await updateDoc(orderRef, {
        label: newLabel,
        updatedAt: new Date().toISOString(),
      });
      setLabel(newLabel);
    } catch (error) {
      console.error("Error actualizando la etiqueta:", error);
      toast({ error, title: t("feedback.labelFailed"), status: "warning" });
    }
  };

  const handleSaveLabel = async () => {
    const trimmedLabel = inputLabel.trim();
    if (trimmedLabel === label) return;
    await updateLabelInOrder(trimmedLabel);
    setInputLabel("");
  };

  const handleRemoveLabel = async () => {
    await updateLabelInOrder("");
    setInputLabel("");
  };

  const generatePlannedRoute = async () => {
    try {
      setGeneratingRoute(true);

      const endpoint = API_Fleet_ID_planned_route.replace("{fleetId}", fleetId).replace(
        "{transportOrderId}",
        transportOrderId
      );

      const checkpoints =
        orderDetail?.checkPoints?.map((cp) => ({
          latitude: Number(cp.latitude),
          longitude: Number(cp.longitude),
        })) || [];

      if (checkpoints.length < 2) {
        toast({
          title: t("orderDetail.plannedRouteAtLeastTwoCheckpoints"),
          status: "warning",
        });
        return;
      }

      await API_REQUEST_HEADERS_BASEURL.post(endpoint, { checkpoints });

      await fetchPlannedRoute();
      setShowPlannedRoute(true);

      toast({
        title: t("orderDetail.plannedRouteGenerated"),
        description: t("orderDetail.plannedRouteGeneratedDescription"),
        status: "success",
      });
    } catch (error) {
      console.error("Erro ao gerar planned route:", error);
      toast({ error,
        title: t("orderDetail.plannedRouteGenerateFailed"),
        status: "error",
      });
    } finally {
      setGeneratingRoute(false);
    }
  };

  const fetchPlannedRoute = async () => {
    try {
      if (!fleetId || !transportOrderId) {
        setPlannedRouteLayer([]);
        return;
      }
      const plannedRouteRef = collection(
        db,
        "fleets",
        fleetId,
        "transport_orders",
        transportOrderId,
        "plannedRoute"
      );
      const plannedRouteSnapshot = await getDocs(plannedRouteRef);
      const planned = plannedRouteSnapshot.docs
        .map((plannedRouteDoc) => ({
          ...plannedRouteDoc.data(),
        }))
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((point) => ({
          latitude: Number(point.latitude),
          longitude: Number(point.longitude),
        }));

      setPlannedRouteLayer(planned);
    } catch (error) {
      console.error("Erro ao buscar planned route:", error);
    }
  };

  const fetchEstimatedGapRoute = async (from, to) => {
    const endpoint = API_Fleet_ID_planned_route_preview.replace("{fleetId}", fleetId);
    const response = await API_REQUEST_HEADERS_BASEURL.post(endpoint, {
      checkpoints: [
        { latitude: from.latitude, longitude: from.longitude },
        { latitude: to.latitude, longitude: to.longitude },
      ],
    });

    const points = Array.isArray(response.data?.plannedRoute) ? response.data.plannedRoute : [];
    return points
      .map((point) => ({
        latitude: toFiniteNumber(point.latitude),
        longitude: toFiniteNumber(point.longitude),
      }))
      .filter(
        (point) =>
          point.latitude != null &&
          point.longitude != null &&
          point.latitude >= -90 &&
          point.latitude <= 90 &&
          point.longitude >= -180 &&
          point.longitude <= 180
      );
  };

  const validRouteLayer = useMemo(
    () =>
      routeLayer.filter(
        (point) =>
          point &&
          point.latitude != null &&
          point.longitude != null &&
          point.latitude >= -90 &&
          point.latitude <= 90 &&
          point.longitude >= -180 &&
          point.longitude <= 180
      ),
    [routeLayer]
  );

  const actualRouteSegments = useMemo(
    () => splitRouteByGap(validRouteLayer, ACTUAL_ROUTE_GAP_THRESHOLD_KM),
    [validRouteLayer]
  );

  useEffect(() => {
    let cancelled = false;

    const loadGapRoutes = async () => {
      if (!isOpen) {
        setEstimatedGapRoutes([]);
        return;
      }

      if (!fleetId || validRouteLayer.length < 2) {
        setEstimatedGapRoutes([]);
        return;
      }

      const gaps = estimateGapPairs(validRouteLayer, ACTUAL_ROUTE_GAP_THRESHOLD_KM);
      if (gaps.length === 0) {
        setEstimatedGapRoutes([]);
        return;
      }

      const estimatedRoutes = await Promise.all(
        gaps.map(async ({ from, to }) => {
          const cacheKey = pointPairCacheKey(from, to);
          const cached = gapRouteCacheRef.current.get(cacheKey);
          if (cached) {
            return cached;
          }

          try {
            const route = await fetchEstimatedGapRoute(from, to);
            const normalizedRoute = route.length >= 2 ? route : [];
            gapRouteCacheRef.current.set(cacheKey, normalizedRoute);
            return normalizedRoute;
          } catch (error) {
            console.error("Error loading estimated gap route:", error);
            gapRouteCacheRef.current.set(cacheKey, []);
            return [];
          }
        })
      );

      if (cancelled) return;
      setEstimatedGapRoutes(estimatedRoutes.filter((route) => route.length >= 2));
    };

    loadGapRoutes().catch((error) => {
      console.error("Error building estimated gap routes:", error);
      if (!cancelled) {
        setEstimatedGapRoutes([]);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [fleetId, isOpen, transportOrderId, validRouteLayer]);

  const lastPoint = validRouteLayer.length
    ? {
        latitude: Number(validRouteLayer[validRouteLayer.length - 1].latitude),
        longitude: Number(validRouteLayer[validRouteLayer.length - 1].longitude),
      }
    : null;

  const orderHasActiveDriver = ["ASSIGNED", "ACTIVE"].includes(
    String(orderDetail?.status || "").toUpperCase()
  );
  const driverMarkerLayer = lastPoint
    ? [
        {
          type: "marker",
          id: "driver",
          markerKind: "driver",
          data: [
            {
              ...lastPoint,
              isOnline: driverPresenceState?.isOnline ?? false,
              hasActiveOrder: Boolean(driverPresenceState?.hasActiveOrder) || orderHasActiveDriver,
            },
          ],
        },
      ]
    : [];

  const editCard = async (transportOrderId, formData) => {
    try {
      const scheduledDate = parseScheduledDate(formData.activationAt);
      if (!scheduledDate) {
        toast({ title: t("validationReviewFields"), status: "warning" });
        return;
      }
      const timestamp = scheduledDate.toISOString();

      const checkPoints = (formData.checkPoints || []).map((cp) => ({
        latitude: parseFloat(cp.latitude),
        longitude: parseFloat(cp.longitude),
        address: cp.address || "",
        name: cp.name || cp.address || "",
        countryCode: cp.countryCode || "",
      }));

      const structuredGoods = Array.isArray(formData.structuredGoods)
        ? formData.structuredGoods
            .map((item) =>
              Object.fromEntries(
                Object.entries(item || {}).filter(
                  ([, value]) => value !== undefined && value !== null && value !== ""
                )
              )
            )
            .filter((item) => Boolean(item.name))
        : formData.goods
        ? [{ name: String(formData.goods).trim() }]
        : [];

      let status = formData.status;
      if (!formData.userId || formData.userId === "") {
        status = "PENDING";
      }

      const shouldGenerateEDocs = Boolean(
        formData.generateEDocs ??
          formData.generateECMR ??
          orderDetail?.generateEDocs ??
          orderDetail?.generateECMR
      );
      const selectedEDocProfile =
        formData.eDocsProfile || formData.transfollow?.edocProfile || "AUTO";
      const firstPoint = checkPoints[0] || {};
      const lastPoint = checkPoints[checkPoints.length - 1] || {};
      const toTransfollowLocation = (location, fallback = {}) => ({
        name: location?.name || fallback.name || fallback.address || "",
        streetName: location?.address?.streetName || fallback.address || "",
        cityName: location?.address?.cityName || "",
        countryCode: location?.address?.countryCode || fallback.countryCode || "",
        latitude: location?.latitude ?? fallback.latitude ?? null,
        longitude: location?.longitude ?? fallback.longitude ?? null,
      });

      const body = {
        transportOrderRef: formData.customerReference || formData.transportOrderRef,
        customerReference: formData.customerReference || formData.transportOrderRef,
        internalReference: formData.internalReference || "",
        label: formData.label || "",
        status,
        type: formData.type || "",
        activationAt: timestamp,
        checkPoints,
        generateEDocs: shouldGenerateEDocs,
        generateECMR: shouldGenerateEDocs,
        directoryRefs: formData.directoryRefs || orderDetail?.directoryRefs || null,
        customer: formData.customer || orderDetail?.customer || null,
        truck: {
          trailerId: formData.trailerId || formData.truck?.trailerId || "",
          headId: formData.headId || formData.truck?.headId || "",
          plateSources: formData.plateSources,
        },
        transfollow: {
          structuredGoods,
          carrierToDriverInstructions: String(formData.driverInstructions || "").trim(),
          specialAgreements: String(formData.specialAgreements || '').trim(),
          edocProfile: selectedEDocProfile,
          ...buildEDocTemplates(selectedEDocProfile),
          placeOfDelivery: toTransfollowLocation(
            formData.dropoffLocation || formData.transfollow?.placeOfDelivery,
            lastPoint
          ),
          placeOfTakingOver: toTransfollowLocation(
            formData.pickupLocation || formData.transfollow?.placeOfTakingOver,
            firstPoint
          ),
          consignor: {
            name: formData.consignor?.name || formData.transfollow?.consignor?.name || "",
            countryCode:
              formData.consignor?.address?.countryCode ||
              formData.transfollow?.consignor?.countryCode ||
              firstPoint.countryCode ||
              "",
          },
          consignee: {
            name:
              formData.consigneePartner?.name ||
              formData.consignee ||
              formData.transfollow?.consignee?.name ||
              "",
            countryCode:
              formData.consigneePartner?.address?.countryCode ||
              formData.transfollow?.consignee?.countryCode ||
              lastPoint.countryCode ||
              "",
          },
        },
      };

      if (!formData.userId || formData.userId === "") {
        body.userId = null;
        body.status = "PENDING";
      } else {
        body.userId = formData.userId;
      }

      try {
        const endpoint = API_FleetDriver_ID_patch_cards_OT.replace(
          "{fleetId}",
          fleetId
        ).replace("{fleetOT}", transportOrderId);

        const response = await API_REQUEST_HEADERS_BASEURL.patch(endpoint, body);

        return response.data;
      } catch (error) { throw error; }
    } catch (error) {
      console.error("Erro ao editar ordem:", error);
      throw error;
    }
  };

  if (!isOpen) return null;

  const latitude = parseFloat(orderDetail?.checkPoints?.[0]?.latitude) || 0;
  const longitude = parseFloat(orderDetail?.checkPoints?.[0]?.longitude) || 0;

  return (
    <>
      <aside className="order-detail-sidepanel">
        <div className="header order-detail-header" style={{ color: headerTextColor, backgroundColor: primaryColor }}>
          <div className="order-detail-panel-toolbar">
            <Button
              type="default"
              icon={<RightOutlined />}
              onClick={onClose}
              className="order-detail-toolbar-btn"
            />
            <Space size={10}>
              {canOpenDriverActions && (
                <>
                  <NotificationBadge
                    fleetId={fleetId}
                    transportOrderId={transportOrderId}
                    type="incident"
                    buttonSize="middle"
                    onClick={() => setIncidentModalOpen(true)}
                    badgeOffset={{ top: "-7.5px", right: "-5px" }}
                  />
                  <Tooltip title={t("chat.writeMessage")}>
                    <Button
                      type="default"
                      icon={<TbBrandWechat size={16} />}
                      className="order-detail-toolbar-btn"
                      onClick={() =>
                        openChat({
                          key: `TO-${transportOrderId}-${orderDetail?.userId}-${transportOrderId || "none"}`,
                          id: orderDetail?.userId,
                          name: `${orderRefValue} - ${resolvedDriverName}`,
                          type: "TO",
                          currentOrder: transportOrderId,
                        })
                      }
                    />
                  </Tooltip>
                </>
              )}
              {orderDetail?.status === "ACTIVE" && (
                <>
                  <ChangeActiveDriver fleetId={fleetId} orderId={transportOrderId} order={orderDetail} onChanged={fetchOrderDetails} />
                  {!hasPublicLink ? (
                    <Tooltip title={t("orderDetail.newLink")}>
                      <Button
                        type="default"
                        icon={<FaLink />}
                        className="order-detail-toolbar-btn"
                        onClick={generatePublicLink}
                      />
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip title={t("orderDetail.copyLink")}>
                        <Button
                          type="default"
                          icon={<FiCopy />}
                          className="order-detail-toolbar-btn"
                          onClick={copyExistingPublicLink}
                        />
                      </Tooltip>
                      <Tooltip title={t("orderDetail.deleteLink")}>
                        <Button
                          type="default"
                          icon={<FaLinkSlash />}
                          className="order-detail-toolbar-btn"
                          onClick={deletePublicLink}
                        />
                      </Tooltip>
                    </>
                  )}
                </>
              )}
              {(orderViewPdfUrl !== "" ||
                (!hasEDoc &&
                  (orderDetail?.status === "PENDING" ||
                    orderDetail?.status === "ASSIGNED" ||
                    orderDetail?.status === "ACTIVE"))) && (
                <Tooltip
                  title={
                    orderViewPdfUrl !== ""
                      ? t("orderDetail.openEDoc", { defaultValue: "Open e-Doc" })
                      : t("orderDetail.generateEDocsNow", {
                          defaultValue: "Generate e-Docs",
                        })
                  }
                >
                  <Button
                    type="default"
                    icon={<IoDocumentTextOutline />}
                    className="order-detail-toolbar-btn"
                    onClick={() =>
                      orderViewPdfUrl !== ""
                        ? openPdf(orderViewPdfUrl)
                        : generateEDocForOrder()
                    }
                  />
                </Tooltip>
              )}
              {(orderDetail?.status === "PENDING" ||
                orderDetail?.status === "ASSIGNED" ||
                orderDetail?.status === "ACTIVE") && (
                <Tooltip title={t("orderDetail.edit")}>
                  <Button
                    type="default"
                    icon={<CiEdit />}
                    className="order-detail-toolbar-btn"
                    onClick={async () => { if (await fetchOrderDetails()) setIsEditing(true); }}
                  />
                </Tooltip>
              )}
            </Space>
          </div>
          <div className="header-title order-detail-header-title" style={{ color: headerTextColor }}>
            <CiDeliveryTruck size={32} style={{ marginRight: 8 }} />
            <div className="order-detail-header-meta">
              <Typography.Text style={{ color: headerTextColor }}>
                <strong style={{ color: headerTextColor }}>{t("orderDetail.internalReference")}:</strong>{" "}
                <span className="header-data" style={{ color: headerTextColor }}>
                  {orderDetail?.internalReference || transportOrderId || "N/A"}
                </span>
              </Typography.Text>
              <Typography.Text style={{ color: headerTextColor }}>
                <strong style={{ color: headerTextColor }}>{t("orderDetail.customerReference")}:</strong>{" "}
                <span className="header-data" style={{ color: headerTextColor }}>
                  {orderDetail?.customerReference || orderDetail?.transportOrderRef || "N/A"}
                </span>
              </Typography.Text>
              <Typography.Text style={{ color: headerTextColor }}>
                {t("orderDetail.consignee")}:{" "}
                <span className="header-data" style={{ color: headerTextColor }}>
                  {orderDetail?.transfollow?.consignee?.name || "N/A"}
                </span>
              </Typography.Text>
              <Typography.Text style={{ color: headerTextColor }}>
                {t("orderDetail.goods")}:{" "}
                <span className="header-data" style={{ color: headerTextColor }}>
                  {orderDetail?.transfollow?.structuredGoods?.map((g) => g.name).join(", ") || "N/A"}
                </span>
              </Typography.Text>
            </div>
          </div>
        </div>
        <div className="order-detail-scroll">
            {loading || !orderDetail ? (
              <div style={{ textAlign: "center", padding: 24 }}>
                <Spin />
                <Typography.Text style={{ display: "block", marginTop: 12 }}>
                  {t("orderDetail.loadingDetails", { defaultValue: "Loading details..." })}
                </Typography.Text>
              </div>
            ) : (
              <>
                <div className="modal-container">
                  <div className="modal-container main">
                    <section className="modal-container data">
                      <div className="modal-detail">
                        <span className="detail-label">{t("orderDetail.status")}:</span>{" "}
                        {formatOrderStatus(orderDetail?.status)}
                      </div>
                      <div className="modal-detail">
                        <span className="detail-label">{t("orderDetail.type")}:</span>{" "}
                        {formatOrderType(orderDetail?.type)}
                      </div>
                      <div className="modal-detail">
                        <span className="detail-label">{t("orderDetail.placeOfDelivery")}:</span>{" "}
                        {orderDetail?.transfollow?.placeOfDelivery?.name || "N/A"}
                      </div>
                      <div className="modal-detail">
                        <span className="detail-label">{t("orderDetail.placeOfTakingOver")}:</span>{" "}
                        {orderDetail?.transfollow?.placeOfTakingOver?.name || "N/A"}
                      </div>
                    </section>
                  </div>

                  <div className="detail-container">
                    <section className="date-detail">
                      <div className="date-info">
                        <div className="modal-detail">
                          <span className="detail-label">{t("orderDetail.start")}:</span>{" "}
                          {formatFirestoreTimestamp(orderDetail.activationAt)}
                        </div>

                        {orderDetail.status === "FINALIZED" && orderDetail.finalizedAt && (
                          <div className="modal-detail">
                            <span className="detail-label">{t("orderDetail.finalizedAt")}:</span>{" "}
                            {formatFirestoreTimestamp(orderDetail.finalizedAt)}
                          </div>
                        )}
                      </div>
                      <div className="date-icon">
                        <LuCalendarCheck className="icon" size={30} strokeWidth={1} />
                      </div>
                    </section>

                    <section className="truck-detail">
                      <div>
                        {driverName && (
                          <div className="modal-detail">
                            <span className="detail-label">{t("orderDetail.driver")}:</span>{" "}
                            <span>{driverName}</span>
                          </div>
                        )}
                        <div className="modal-detail">
                          <span className="detail-label">{t("orderDetail.truckId")}:</span>{" "}
                          {orderDetail?.truck?.headId || "N/A"}
                        </div>
                        <div className="modal-detail">
                          <span className="detail-label">{t("orderDetail.trailerId")}:</span>{" "}
                          {orderDetail?.truck?.trailerId || "N/A"}
                        </div>
                      </div>
                      <div>
                        <CiDeliveryTruck className="icon" size={30} />
                      </div>
                    </section>
                    {showTruckflow && truckflowPointRows.length > 0 && (
                      <section className="truck-detail">
                        <div>
                          <div className="modal-detail">
                            <span className="detail-label">
                              {t("orderDetail.route", { defaultValue: "Truckflow points" })}:
                            </span>
                          </div>
                          {truckflowPointRows.map(({ label: rowLabel, point }, idx) => (
                            <div key={`${rowLabel}-${idx}`} className="modal-detail">
                              <span className="detail-label">{rowLabel}:</span>{" "}
                              {point?.name || point?.address || `${point?.latitude}, ${point?.longitude}`}
                            </div>
                          ))}
                        </div>
                      </section>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 24 }}>
                  <span className="detail-label">{t("orderDetail.label") || "Etiqueta"}:</span>{" "}
                  {label && (
                    <Tag
                      color="blue"
                      closable={
                        orderDetail?.status !== "FINALIZED" &&
                        orderDetail?.status !== "ARCHIVED"
                      }
                      onClose={handleRemoveLabel}
                    >
                      {label}
                    </Tag>
                  )}
                  {orderDetail?.status !== "FINALIZED" && orderDetail?.status !== "ARCHIVED" && (
                    <div style={{ marginTop: 8 }}>
                      <Input
                        placeholder={t("orderDetail.addLabel") || "Añadir etiqueta"}
                        value={inputLabel}
                        onChange={(e) => setInputLabel(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSaveLabel();
                          }
                        }}
                      />
                    </div>
                  )}
                </div>

                {showTruckflow && (
                  <div className="modal-checklist">
                    <Divider className="modal-divider"></Divider>
                    <ProgressStep
                      type={orderDetail.type}
                      records={
                        orderDetail.truckflow?.transitions ||
                        orderDetail.truckflow?.transitionRecords ||
                        orderDetail.shipmentData?.transitionRecords ||
                        item.raw.truckflow?.transitions ||
                        item.raw.truckflow?.transitionRecords ||
                        []
                      }
                      anomalies={
                        orderDetail.truckflow?.anomalies ||
                        orderDetail.truckflow?.transitionAnomalies ||
                        orderDetail.shipmentData?.transitionAnomalies ||
                        item.raw.truckflow?.anomalies ||
                        item.raw.truckflow?.transitionAnomalies ||
                        []
                      }
                      currentState={
                        orderDetail.truckflow?.state ||
                        orderDetail.shipmentData?.state ||
                        item.raw.truckflow?.state
                      }
                    />
                  </div>
                )}
                <div className="modal-imageGallery">
                  <Divider className="modal-divider"></Divider>

                  {latitude !== 0 && longitude !== 0 && (
                    <div style={{ marginTop: 16 }}>
                      <Typography.Text strong>
                        {t("orderDetail.route") || "Map"}:
                      </Typography.Text>
                      <div className="map-control" style={{ marginTop: 8, borderRadius: 8, overflow: "hidden" }}>
                        <div style={{ display: "flex", gap: 8, marginTop: 16, paddingBottom: 10, justifyContent: "flex-end" }}>
                          <Tooltip title={t("orderDetail.generatePlannedRoute")}>
                            <Button
                              type="primary"
                              icon={<MdOutlineAddRoad />}
                              loading={generatingRoute}
                              onClick={generatePlannedRoute}
                            />
                          </Tooltip>

                          <Tooltip title={t("orderDetail.showPlannedRoute")}>
                            <Button
                              type={showPlannedRoute ? "primary" : "default"}
                              icon={<GiRoad />}
                              onClick={() => setShowPlannedRoute(!showPlannedRoute)}
                            />
                          </Tooltip>
                        </div>

                        <MapWithLayers
                          latitude={latitude}
                          longitude={longitude}
                          layers={[
                            ...actualRouteSegments.map((segment, index) => ({
                              id: `routeLayer-${index}`,
                              type: "polyline",
                              color: actualRouteColor,
                              width: 5,
                              data: segment,
                            })),
                            ...estimatedGapRoutes.map((segment, index) => ({
                              id: `estimatedGapLayer-${index}`,
                              type: "polyline",
                              color: PLANNED_OR_ESTIMATED_ROUTE_COLOR,
                              width: 4,
                              data: segment,
                            })),
                            showPlannedRoute && {
                              id: "plannedRouteLayer",
                              type: "polyline",
                              color: PLANNED_OR_ESTIMATED_ROUTE_COLOR,
                              width: 4,
                              data: plannedRouteLayer,
                            },

                            ...driverMarkerLayer,
                          ].filter(Boolean)}
                        />
                      </div>
                    </div>
                  )}

                  <ImageGallery
                    type={"transportOrder"}
                    fleetId={fleetId}
                    userId={orderDetail?.userId}
                    transportOrderId={transportOrderId}
                  />
                </div>
              </>
            )}
        </div>
      </aside>

      <IncidentModal
        isOpen={isIncidentModalOpen}
        onClose={() => setIncidentModalOpen(false)}
        transportOrderId={transportOrderId}
        transportOrderRef={orderRefValue}
        fleetId={fleetId}
        driver={resolvedDriverName}
      />

      <Modal
        open={isEditing}
        onCancel={() => { setIsEditing(false); fetchOrderDetails(); }}
        footer={null}
        width="min(1180px, calc(100vw - 32px))"
        centered
      >
        <Typography.Title level={4} style={{ textAlign: "center", marginBottom: 16 }}>
          {t("orderDetail.editTransportOrder")}
        </Typography.Title>
        {orderDetail && (
          <OrderForm
            mode="edit"
            initialValues={orderDetail}
            defaultGenerateEDocs={Boolean(orderDetail?.generateEDocs ?? orderDetail?.generateECMR)}
            onSubmit={async (updatedOrder) => {
              try {
                const success = await editCard(transportOrderId, updatedOrder);
                if (success) {
                  toast({
                    title: t("toast.editOrderSuccess"),
                    status: "success",
                  });

                  setIsEditing(false);
                  fetchOrderDetails();
                } else {
                  toast({
                    title: t("toast.editOrderError"),
                    status: "warning",
                  });
                }
              } catch (error) {
                console.error("Erro ao salvar ordem:", error);
                toast({ error,
                  title: t("toast.editOrderError"),
                  status: "warning",
                });
              }
            }}
            onCancel={() => { setIsEditing(false); fetchOrderDetails(); }}
          />
        )}
      </Modal>
    </>
  );
};

export default TransportOrderDetaiModal;
